import { DestroyRef, Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, map, of, shareReplay } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { EventMessage, EventType, AuthenticationResult, AccountInfo } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { GRAPH_API } from '@constants/graph-api.constants';
import { MANUAL_TOKEN_KEY } from '@constants/auth.constants';
import { ManualTokenStateService } from './coding/manual-token-state.service';
import { ConfigService } from './config.service';
import { ChatHistoryService } from './chat-history.service';
import {
  AMS_ATTENDANT_ROLE,
  AMS_MANAGER_ROLE,
  PLATFORM_ADMIN_ROLE,
  TEAM_TRACKING_MANAGER_ROLE,
  PlatformRoleName,
  UserPlatformProfile,
} from '@models/admin/platform-profile.model';

export interface UserProfile {
  name?: string;
  username?: string;
  id?: string;
  photoUrl?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private platformId = inject<object>(PLATFORM_ID);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private http = inject(HttpClient);
  private readonly config = inject(ConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly manualTokenState = inject(ManualTokenStateService);
  private readonly chatHistoryService = inject(ChatHistoryService);

  private loggedInSubject = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this.loggedInSubject.asObservable();

  private userProfileSubject = new BehaviorSubject<UserProfile | null>(null);
  public userProfile$ = this.userProfileSubject.asObservable();

  // Sinalizado explicitamente em setMsalUser (não derivado via computed a
  // partir de msalService.instance.getActiveAccount()), pois essa chamada não
  // é reativa a um signal — um computed sem dependência de signal calcularia
  // o valor uma única vez e nunca refletiria uma troca de conta na sessão.
  private readonly activeUserId = signal<string | null>(null);
  readonly currentUserId = this.activeUserId.asReadonly();

  // undefined = ainda não carregado; null = falha ou sem perfil; UserPlatformProfile = carregado
  private readonly userPlatformProfileSubject = new BehaviorSubject<
    UserPlatformProfile | null | undefined
  >(undefined);
  public readonly userPlatformProfile$ = this.userPlatformProfileSubject.asObservable();

  private createRoleStream(...roles: PlatformRoleName[]): Observable<boolean> {
    return this.userPlatformProfileSubject.pipe(
      filter((p): p is UserPlatformProfile | null => p !== undefined),
      map(
        (p) =>
          this.manualTokenState.isActive() ||
          (p?.roles?.some((r) => roles.includes(r.name as PlatformRoleName)) ?? false)
      ),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }

  public readonly isPlatformAdmin$: Observable<boolean> =
    this.createRoleStream(PLATFORM_ADMIN_ROLE);
  public readonly canAccessAms$: Observable<boolean> = this.createRoleStream(
    PLATFORM_ADMIN_ROLE,
    AMS_MANAGER_ROLE,
    AMS_ATTENDANT_ROLE
  );
  // Curadoria do acervo (editar, excluir, aprovar documento) fica restrita a Admin e Gestor AMS.
  public readonly canManageAms$: Observable<boolean> = this.createRoleStream(
    PLATFORM_ADMIN_ROLE,
    AMS_MANAGER_ROLE
  );
  // Cadastro de FAQ e documento: o atendente alimenta a base dos clientes que atende. O recorte
  // por cliente é aplicado no servidor — aqui só se decide se o ponto de entrada aparece.
  public readonly canContributeAms$: Observable<boolean> = this.createRoleStream(
    PLATFORM_ADMIN_ROLE,
    AMS_MANAGER_ROLE,
    AMS_ATTENDANT_ROLE
  );
  // Administrador da Plataforma acessa Acompanhamento de Equipe sem registro explícito como Gestor.
  public readonly isTeamTrackingManager$: Observable<boolean> = this.createRoleStream(
    PLATFORM_ADMIN_ROLE,
    TEAM_TRACKING_MANAGER_ROLE
  );

  private userPhoto$: Observable<string | null> | null = null;
  private currentPhotoUrl: string | null = null;
  private platformProfileLoaded = false;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeAuth();
    }
  }

  private initializeAuth() {
    this.checkManualLoginStatus();

    this.msalService.initialize().subscribe(() => {
      this.msalService.handleRedirectObservable().subscribe((result) => {
        if (result) {
          this.setMsalUser(result.account);
          this.loggedInSubject.next(true);
        }
      });

      this.msalBroadcastService.msalSubject$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result: EventMessage) => {
          if (
            (result.eventType === EventType.LOGIN_SUCCESS ||
              result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
            result.payload
          ) {
            const payload = result.payload as AuthenticationResult;
            this.setMsalUser(payload.account);
            this.loggedInSubject.next(true);
          }
        });

      const activeAccount = this.msalService.instance.getActiveAccount();
      if (activeAccount) {
        this.setMsalUser(activeAccount);
        this.loggedInSubject.next(true);
      } else {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
          this.msalService.instance.setActiveAccount(accounts[0]);
          this.setMsalUser(accounts[0]);
          this.loggedInSubject.next(true);
        }
      }
    });
  }

  checkManualLoginStatus() {
    this.manualTokenState.activate();
    if (this.manualTokenState.isActive()) {
      this.loggedInSubject.next(true);
      this.userProfileSubject.next({ name: 'Manual User', username: 'manual' });
      // Tokens manuais não têm perfil de plataforma — tratar como sem permissão
      this.userPlatformProfileSubject.next(null);
    }
  }

  private setMsalUser(account: AccountInfo) {
    this.msalService.instance.setActiveAccount(account);
    this.activeUserId.set(account.localAccountId);

    const current = this.userProfileSubject.getValue();

    if (current?.id !== account.localAccountId) {
      if (this.currentPhotoUrl) {
        URL.revokeObjectURL(this.currentPhotoUrl);
        this.currentPhotoUrl = null;
      }
      this.userPhoto$ = null;
      this.platformProfileLoaded = false;
    }

    this.userProfileSubject.next({
      name: account.name,
      username: account.username,
      id: account.localAccountId,
    });

    if (environment.features.enableMsalProfilePhoto) {
      this.fetchUserPhoto();
    }

    if (!this.platformProfileLoaded) {
      this.platformProfileLoaded = true;
      this.loadPlatformProfile();
    }
  }

  private loadPlatformProfile(): void {
    this.http
      .get<UserPlatformProfile>(`${this.config.apiEndpoint}/admin/profile`)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => of(null))
      )
      .subscribe((profile) => {
        this.userPlatformProfileSubject.next(profile);
      });
  }

  private fetchUserPhoto(): void {
    if (this.userPhoto$) return;

    this.userPhoto$ = this.http.get(GRAPH_API.photo, { responseType: 'blob' }).pipe(
      map((blob) => URL.createObjectURL(blob)),
      catchError(() => of(null)),
      shareReplay(1)
    );

    this.userPhoto$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((url) => {
      if (this.currentPhotoUrl) URL.revokeObjectURL(this.currentPhotoUrl);
      this.currentPhotoUrl = url;
      const current = this.userProfileSubject.getValue();
      this.userProfileSubject.next({ ...(current ?? {}), photoUrl: url ?? undefined });
    });
  }

  loginWithMicrosoft() {
    this.msalService.loginRedirect();
  }

  loginWithManualToken(token: string) {
    localStorage.setItem(MANUAL_TOKEN_KEY, token);
    this.checkManualLoginStatus();
  }

  logout() {
    this.chatHistoryService.clearSessionData();
    if (localStorage.getItem(MANUAL_TOKEN_KEY)) {
      localStorage.removeItem(MANUAL_TOKEN_KEY);
      this.manualTokenState.deactivate();
      this.loggedInSubject.next(false);
      this.userProfileSubject.next(null);
      this.userPlatformProfileSubject.next(undefined);
      this.activeUserId.set(null);
      this.platformProfileLoaded = false;
      window.location.reload();
    } else {
      this.msalService.logoutRedirect({
        postLogoutRedirectUri: environment.auth.msalConfig?.postLogoutRedirectUri,
      });
    }
  }

  getManualToken(): string | null {
    return localStorage.getItem(MANUAL_TOKEN_KEY);
  }

  testSetPlatformProfile(profile: UserPlatformProfile | null): void {
    if (!environment.production) {
      this.userPlatformProfileSubject.next(profile);
    }
  }

  isAuthenticated(): boolean {
    const isManual = !!this.getManualToken();
    const isMsal =
      this.msalService.instance && this.msalService.instance.getAllAccounts().length > 0;
    return isManual || isMsal;
  }
}
